import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Typography,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import HttpService from "../../shared/service/http-service";
import CustomTabPanel from "../../shared/tab-panel/tab-panel";
import { AcademicDegreeDTO, GopDTO } from "../dictionaries/models";
import { PeriodDTO, ReportStatus, ReportUniversityStatusDTO, UniversityDTO } from "../report/models";
import {
  DashboardDTO,
  DashboardStudentStatsDTO,
  ScholarshipSummaryDTO,
  ScholarshipSummaryFilter,
  UniversityStatusRow,
} from "./model";
import PeriodStatusTable from "./table/period-status-table";
import ScholarshipSummary from "./table/scholarship-summary";
import UniversityStatusTable from "./table/university-status-table";
import UniversityStudentStatsTable from "./table/university-student-stats-table";

const filterFieldSx = {
  width: "100%",
  "& .MuiInputBase-root": { height: 40 },
};

const hasYear = (period?: PeriodDTO) => Boolean(period?.year);

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

function universityLabel(university: UniversityDTO) {
  return university.nameRu || university.shortNameRu || "—";
}

function emptyUniversityRow(name: string): UniversityStatusRow {
  return {
    universityName: name,
    total: 0,
    statuses: [],
    has324: false,
    students: 0,
  };
}

function aggregateByUniversity(reports: ReportUniversityStatusDTO[]): UniversityStatusRow[] {
  const map = new Map<string, UniversityStatusRow>();
  for (const report of reports) {
    const name = report.universityName || "—";
    const row = map.get(name) ?? emptyUniversityRow(name);
    row.total += 1;
    row.students += report.numberOfStudents ?? 0;
    row.has324 = row.has324 || Boolean(report.hasFundingId4);
    if (
      report.status &&
      !row.statuses.includes(report.status as ReportStatus)
    ) {
      row.statuses.push(report.status as ReportStatus);
    }
    map.set(name, row);
  }
  return Array.from(map.values());
}

function mergeUniversityRows(
  reports: ReportUniversityStatusDTO[],
  universities: UniversityDTO[]
): UniversityStatusRow[] {
  const fromReports = aggregateByUniversity(reports);
  const byName = new Map(
    fromReports.map((row) => [row.universityName, row])
  );
  const knownNames = new Set(universities.map(universityLabel));
  const rows = universities.map((university) => {
    const name = universityLabel(university);
    return (
      byName.get(name) ?? emptyUniversityRow(name)
    );
  });
  for (const row of fromReports) {
    if (!knownNames.has(row.universityName)) {
      rows.push(row);
    }
  }
  return rows.sort((a, b) => {
    if (b.total !== a.total) return b.total - a.total;
    return a.universityName.localeCompare(b.universityName, "ru");
  });
}

const Dashboard: React.FC = () => {
  const [periods, setPeriods] = useState<PeriodDTO[]>([]);
  const [data, setData] = useState<DashboardDTO>();
  const [universityRows, setUniversityRows] = useState<UniversityStatusRow[]>(
    []
  );
  const [universities, setUniversities] = useState<UniversityDTO[]>([]);
  const [uniLoading, setUniLoading] = useState(false);
  const [studentStats, setStudentStats] = useState<DashboardStudentStatsDTO>();
  const [statsLoading, setStatsLoading] = useState(false);
  const [scholarshipSummary, setScholarshipSummary] = useState<ScholarshipSummaryDTO>();
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryFilter, setSummaryFilter] = useState<ScholarshipSummaryFilter>({});
  const [degrees, setDegrees] = useState<AcademicDegreeDTO[]>([]);
  const [gops, setGops] = useState<GopDTO[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodDTO>({
    year: dayjs(new Date()).year(),
  });
  const [value, setValue] = React.useState(0);
  const summaryRequestSeq = useRef(0);

  const loadPeriod = () => {
    HttpService.getDashboardData(selectedPeriod).then((resp) =>
      setData(resp.data)
    );
  };

  const loadUniversities = () => {
    if (!hasYear(selectedPeriod)) {
      setUniversityRows([]);
      return;
    }
    setUniLoading(true);
    const unisPromise =
      universities.length > 0
        ? Promise.resolve(universities)
        : HttpService.getUniversities().then((resp) => {
            const list = resp.data ?? [];
            setUniversities(list);
            return list;
          });
    Promise.all([
      HttpService.getReportUniversityStatuses({ period: selectedPeriod }).then((resp) => resp.data ?? []),
      unisPromise,
    ])
      .then(([reports, unis]) =>
        setUniversityRows(mergeUniversityRows(reports, unis))
      )
      .catch(() => {
        setUniversityRows([]);
      })
      .finally(() => setUniLoading(false));
  };

  const loadStudentStats = () => {
    if (!hasYear(selectedPeriod)) {
      setStudentStats(undefined);
      return;
    }
    setStatsLoading(true);
    HttpService.getDashboardStudentStats(selectedPeriod)
      .then((resp) => setStudentStats(resp.data))
      .catch(() => {
        setStudentStats(undefined);
      })
      .finally(() => setStatsLoading(false));
  };

  const loadScholarship = () => {
    if (!hasYear(selectedPeriod)) {
      setScholarshipSummary(undefined);
      return;
    }
    const seq = ++summaryRequestSeq.current;
    setSummaryLoading(true);
    HttpService.getScholarshipSummary(selectedPeriod, summaryFilter)
      .then((resp) => {
        if (seq === summaryRequestSeq.current) {
          setScholarshipSummary(resp.data);
        }
      })
      .catch(() => {
        if (seq === summaryRequestSeq.current) {
          setScholarshipSummary(undefined);
        }
      })
      .finally(() => {
        if (seq === summaryRequestSeq.current) {
          setSummaryLoading(false);
        }
      });
  };

  const loadTab = (tab: number) => {
    if (tab === 0) {
      loadPeriod();
    } else if (tab === 1) {
      loadUniversities();
    } else if (tab === 2) {
      loadStudentStats();
    } else if (tab === 3) {
      loadScholarship();
    }
  };

  useEffect(() => {
    HttpService.getPeriods().then((resp) => {
      if (resp.data.length > 0) {
        const updatedPeriods = resp.data.map((period) => ({
          ...period,
          displayValue: `${period?.day} ${period.monthValue}`,
        }));
        setPeriods(updatedPeriods);
      }
    });
    HttpService.getUniversities().then(
      (resp) => setUniversities(resp.data ?? []),
      () => undefined
    );
  }, []);

  useEffect(() => {
    loadTab(value);
  }, [selectedPeriod]);

  useEffect(() => {
    if (value === 3) {
      loadScholarship();
    }
  }, [summaryFilter]);

  useEffect(() => {
    if (value !== 3) {
      return;
    }
    if (degrees.length === 0) {
      HttpService.getDegrees().then(
        (resp) => setDegrees(resp.data ?? []),
        () => undefined
      );
    }
    if (gops.length === 0) {
      HttpService.getGops().then(
        (resp) => setGops(resp.data ?? []),
        () => undefined
      );
    }
  }, [value]);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    loadTab(newValue);
  };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "flex-end",
          gap: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1.3,
            mr: "auto",
            alignSelf: "center",
            color: "text.primary",
          }}
        >
          Статистика отчётов
        </Typography>
        <FormControl
          size="small"
          sx={{ flex: "1 1 160px", minWidth: 140, maxWidth: 240 }}
        >
          <InputLabel id="dashboard-period-label" shrink>
            Дата отчета
          </InputLabel>
          <Select
            labelId="dashboard-period-label"
            label="Дата отчета"
            notched
            displayEmpty
            value={selectedPeriod?.displayValue ?? ""}
            sx={filterFieldSx}
            onChange={(e) => {
              const stringValue = e.target.value as string;
              if (stringValue && stringValue.length > 0) {
                const period = periods.find(
                  (per) => per.displayValue === stringValue
                );
                if (period) {
                  setSelectedPeriod({ ...period, year: selectedPeriod.year });
                }
              } else {
                setSelectedPeriod({ year: selectedPeriod.year });
              }
            }}
          >
            <MenuItem value="">Не выбрано</MenuItem>
            {periods && periods.length > 0
              ? periods.map((period) => (
                  <MenuItem
                    value={period.displayValue}
                    key={period.displayValue}
                  >
                    {period.displayValue}
                  </MenuItem>
                ))
              : null}
          </Select>
        </FormControl>

        <DatePicker
          label="Год"
          views={["year"]}
          value={
            selectedPeriod?.year != null
              ? dayjs(new Date(selectedPeriod.year, 0, 1))
              : null
          }
          onChange={(e) => {
            if (e) {
              setSelectedPeriod({ ...selectedPeriod, year: e.year() });
            } else {
              setSelectedPeriod({ ...selectedPeriod, year: undefined });
            }
          }}
          slotProps={{
            textField: {
              size: "small",
              sx: { ...filterFieldSx, width: 120 },
              InputLabelProps: { shrink: true },
            },
          }}
        />
        <Button
          variant="contained"
          size="medium"
          sx={{ height: 40, flex: "0 0 auto" }}
          onClick={() => loadTab(value)}
        >
          Поиск
        </Button>
      </Box>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={value} onChange={handleChange}>
          <Tab label="Период" {...a11yProps(0)} />
          <Tab label="По вузам" {...a11yProps(1)} />
          <Tab label="Студенты" {...a11yProps(2)} />
          <Tab label="Стипендии" {...a11yProps(3)} />
        </Tabs>
      </Box>
      <Box sx={{ width: "100%" }}>
        <CustomTabPanel value={value} index={0}>
          <PeriodStatusTable data={data} />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          {!hasYear(selectedPeriod) ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Выберите год, чтобы увидеть разбивку по вузам.
            </Typography>
          ) : uniLoading ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Загрузка…
            </Typography>
          ) : (
            <UniversityStatusTable rows={universityRows} />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={2}>
          {!hasYear(selectedPeriod) ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Выберите год, чтобы увидеть студентов по вузам.
            </Typography>
          ) : statsLoading ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Загрузка…
            </Typography>
          ) : (
            <UniversityStudentStatsTable
              data={studentStats}
              periodLabel={`${selectedPeriod.displayValue ?? ""} ${
                selectedPeriod.year
              }`.trim()}
            />
          )}
        </CustomTabPanel>
        <CustomTabPanel value={value} index={3}>
          {!hasYear(selectedPeriod) ? (
            <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
              Выберите год, чтобы увидеть сводку по стипендиям.
            </Typography>
          ) : (
            <ScholarshipSummary
              data={scholarshipSummary}
              loading={summaryLoading}
              periodLabel={`${selectedPeriod.displayValue ?? ""} ${
                selectedPeriod.year
              }`.trim()}
              degrees={degrees}
              universities={universities}
              gops={gops}
              filter={summaryFilter}
              onChange={setSummaryFilter}
              onReset={() => setSummaryFilter({})}
            />
          )}
        </CustomTabPanel>
      </Box>
    </Box>
  );
};

export default Dashboard;
